import _ from "lodash";
import { atom, selector } from "recoil";

import * as api from "./api";

import { Sequencer } from "./Sequencer";

import { initialLoopMode, OptionsStore } from "./OptionsStore";

import defaultPlaylist from "./data/playlist.json";

import { trackTitle } from "./utils";
import { applyFilter, factoryFilter } from "./filter";
import { loadAlbumsCache } from "./cache";

export const playlist = atom({
  key: "playlist",
  default: [],
});

const playlistFilterAtom = atom({
  key: "playlistFilterAtom",
  default: factoryFilter(),
});

export const playlistFilter = selector({
  key: "playlistFilter",
  get: ({ get }) => get(playlistFilterAtom),
  set: ({ set, get }, newFilter) => {
    set(playlistFilterAtom, newFilter);

    const playlist = get(currentPlaylist);
    const selected = applyFilter(playlist, newFilter);

    Sequencer.listUpdated(selected);
  },
});

export const playlistFilterActive = selector({
  key: "playlistFilterActive",
  get: ({ get }) => {
    const filter = get(playlistFilter);

    return !_.isEqual(filter, factoryFilter());
  },
});

export const currentTitle = atom({
  key: "currentTitle",
  default: "",
});

export const titleOptions = atom({
  key: "titleOptions",
  default: [],
});

export const customPlaylist = atom({
  key: "customPlaylist",
  default: { title: "", playlist: [] },
});

export const currentPlaylist = selector({
  key: "currentPlaylist",
  get: ({ get }) => get(playlist),
  set: ({ set, get }, newList) => {
    const filter = get(playlistFilter);
    const selected = applyFilter(newList, filter);

    Sequencer.listUpdated(selected);

    const titles = extractTitles(newList);
    set(titleOptions, titles);

    set(playlist, newList);
  },
});

export const sortedPlaylist = selector({
  key: "sortedPlaylist",
  get: ({ get }) => {
    const list = get(currentPlaylist);

    return [...list]
      .filter((track) => !track.error)
      .sort((a, b) => a.title.localeCompare(b.title));
  },
});

function extractTitles(list) {
  return list
    .map((t) => {
      const title = trackTitle(t);

      return {
        key: t.id,
        text: title,
        value: t.id,
      };
    })
    .sort((a, b) => a.text.localeCompare(b.text));
}

const albumsCache = selector({
  key: "albumsCache",
  get: async () => {
    return await loadAlbumsCache()
  }
})

export const currentPlaylistUrl = atom({
  key: "currentPlaylistUrl",
  default: null,
});

export const playlistLoader = selector({
  key: "playlistLoader",
  get: async ({ get }) => {
    const audiCache = get(albumsCache);

    let definition = null;

    let description = "";

    const url = get(currentPlaylistUrl);

    if (url === "custom:") {
      definition = get(customPlaylist);
    } else if (url) {
      definition = await api.loadPlaylist(url);

      description = await api.loadPlaylistDescription(url, definition);
    } else {
      definition = api.convertList(defaultPlaylist);
    }

    const { title, playlist } = definition;

    if (title) document.title = title;

    return { title, playlist, description, audiCache };
  },
});

export const requestedTrack = atom({
  key: "requestedTrack",
  default: null,
});

export const songsMetadata = atom({
  key: "songsMetadata",
  default: { content: "", metadata: {} },
});

export const viewingDescription = atom({
  key: "viewingDescription",
  default: false,
});

export const showTimeline = atom({
  key: "showTimeline",
  default: false,
});

export const viewingMobileTransport = atom({
  key: "viewingMobileTransport",
  default: false,
});

export const lyricsActive = atom({
  key: "lyricsActive",
  default: OptionsStore.restore().lyricsActive,
});

export const cardFormat = atom({
  key: "cardFormat",
  default: OptionsStore.restore().cardFormat,
});

export const snoozeModal = atom({
  key: "snoozeModal",
  default: false,
});

export const screenMode = atom({
  key: "screenMode",
  default: "normal",
});

export const playbackMode = atom({
  key: "playbackMode",
  default: initialLoopMode(),
});

export const draggableTracks = atom({
  key: "draggableTracks",
  default: false,
});

export const viewingReorder = atom({
  key: "viewingReorder",
  default: false,
});

export const viewingVolumes = atom({
  key: "viewingVolumes",
  default: false,
});
